import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { TourStep } from './steps';
import { findPresentStep, stepPosition } from './tour';
import { btns } from '../components/buttons';
import { useFocusTrap } from '../lib/useFocusTrap';

// Spotlight geometry: breathing room around the anchor, spacing to the card,
// and a viewport safe margin the card never crosses.
const PAD = 6;
const GAP = 14;
const CARD_W = 340;
const MARGIN = 16;

interface Rect { top: number; left: number; width: number; height: number }

/** The anchor's viewport rect, padded — or null when it's gone/invisible. */
function measureTarget(target: string): Rect | null {
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

/**
 * The tour's spotlight + narration card. One anchor is lit at a time: a
 * bronze-edged cutout (the classic single-div box-shadow scrim, so the dimmed
 * field and the opening are one element that glides between steps) with a calm
 * card placed beside it. The full-viewport root intercepts pointer events, so
 * the page underneath is read-only while the tour runs — no stray clicks.
 *
 * Keyboard: Esc leaves the tour, arrow keys move between steps, and focus
 * follows the card so screen readers announce each stop.
 */
export function TourOverlay({ steps, index, isPresent, onNext, onBack, onSkip }: {
  steps: TourStep[];
  index: number;
  isPresent: (target: string) => boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const step = steps[index]!;
  // undefined = not yet measured (first frame); null = anchor vanished.
  const [rect, setRect] = useState<Rect | null | undefined>(undefined);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // rAF-coalesced: scroll (capture, so nested containers count) and resize can
  // fire dozens of times a frame-second; one measurement per paint is plenty
  // and keeps the forced-layout reads off the scroll hot path.
  const rafId = useRef(0);
  const remeasure = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      setRect(measureTarget(step.target));
    });
  }, [step.target]);

  // On step change: bring the anchor into view (instantly — a smooth scroll
  // would race the measurement), then measure it synchronously.
  useLayoutEffect(() => {
    setCardPos(null);
    document.querySelector(`[data-tour="${step.target}"]`)?.scrollIntoView?.({ block: 'nearest' });
    setRect(measureTarget(step.target));
  }, [step.target]);

  // The spotlight tracks the anchor through resizes and scrolls.
  useEffect(() => {
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = 0;
    };
  }, [remeasure]);

  // Anchor vanished mid-step (e.g. its card unmounted) — glide on rather than
  // spotlight an empty patch of page.
  useEffect(() => {
    if (rect === null) onNext();
  }, [rect, onNext]);

  // Place the card once its real height is known: beside a left-rail anchor,
  // otherwise below the spotlight, above when there's no room, centered as a
  // last resort. Runs after render so cardRef has layout.
  useLayoutEffect(() => {
    if (!rect || !cardRef.current) return;
    const ch = cardRef.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = Math.min(CARD_W, vw - MARGIN * 2);
    const clampX = (x: number) => Math.min(Math.max(x, MARGIN), Math.max(MARGIN, vw - cw - MARGIN));
    let top: number;
    let left: number;
    if (rect.left + rect.width < 340 && vw >= 1024) {
      left = rect.left + rect.width + GAP;
      top = Math.min(Math.max(rect.top, MARGIN), Math.max(MARGIN, vh - ch - MARGIN));
    } else if (rect.top + rect.height + GAP + ch + MARGIN <= vh) {
      top = rect.top + rect.height + GAP;
      left = clampX(rect.left);
    } else if (rect.top - GAP - ch >= MARGIN) {
      top = rect.top - GAP - ch;
      left = clampX(rect.left);
    } else {
      top = Math.max(MARGIN, (vh - ch) / 2);
      left = Math.max(MARGIN, (vw - cw) / 2);
    }
    setCardPos({ top, left });
  }, [rect, index]);

  // Traps Tab within the tour surface (PS2) — declared before the
  // "focus follows the card" effect below so that effect's more specific
  // per-step focus wins (effects run in declaration order on mount).
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // Focus follows the card (aria-live isn't needed; the card is the dialog).
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [index]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onBack();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onSkip, onNext, onBack]);

  const { n, of } = stepPosition(steps, index, isPresent);
  const isLast = findPresentStep(steps, index + 1, 1, isPresent) === null;
  const hasBack = findPresentStep(steps, index - 1, -1, isPresent) !== null;

  return createPortal((
    <div
      data-testid="tour"
      role="dialog"
      aria-modal="true"
      aria-label={`Tour: ${step.title}`}
      className="fixed inset-0 z-[70]"
      ref={trapRef}
    >
      {/* Spotlight — the box-shadow floods everything around the cutout. */}
      {rect ? (
        <div
          aria-hidden
          className="absolute rounded-token border border-bronze/70 shadow-[0_0_0_9999px_rgba(15,23,42,0.45)] transition-[top,left,width,height] duration-300 ease-out motion-reduce:transition-none"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-ink/45" />
      )}

      {/* Narration card — invisible until placed (its height decides where). */}
      <div
        ref={cardRef}
        tabIndex={-1}
        data-testid="tour-card"
        className="absolute w-[340px] max-w-[calc(100vw-32px)] rounded-token border border-line bg-surface p-5 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)] outline-none transition-[top,left,opacity] duration-300 ease-out motion-reduce:transition-none"
        style={cardPos ? { top: cardPos.top, left: cardPos.left, opacity: 1 } : { top: 0, left: 0, opacity: 0 }}
      >
        <button
          onClick={onSkip}
          aria-label="Close tour"
          className="absolute right-3 top-3 flex h-7 w-7 cursor-pointer items-center justify-center rounded-token border-none bg-transparent p-0 text-slate transition-colors duration-150 hover:text-navy"
        >
          <X size={15} strokeWidth={1.75} aria-hidden />
        </button>
        <div className="mb-2 font-sans text-[10px] font-semibold uppercase tracking-[0.12em] text-slate">
          {n} of {of}
        </div>
        <h2 className="m-0 mb-1.5 font-serif text-[17px] font-normal text-navy">{step.title}</h2>
        <p className="m-0 mb-4 font-sans text-[13px] leading-relaxed text-mid">{step.body}</p>
        <div className="flex items-center gap-2">
          {!isLast && (
            <button
              onClick={onSkip}
              className="cursor-pointer border-none bg-transparent p-0 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-slate transition-colors duration-150 hover:text-navy"
            >
              Skip for now
            </button>
          )}
          <div className="ml-auto flex gap-2">
            {hasBack && <button className={btns.btn} onClick={onBack}>Back</button>}
            <button className={btns.btnPrim} onClick={onNext} data-testid="tour-next">
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
